import { makeVar, useReactiveVar } from '@apollo/client';
import { SidebarTool } from '../utils/SidebarTool';
import { getSession } from '../services/authentication';
import { Session } from '../services/models/session';

// Client-side application state, previously the last remaining Redux slices.
// Apollo reactive vars keep the same update-and-rerender semantics the
// store provided without the store.

/**
 * Auth session: the token + account pair, or null when signed out. The
 * signed-in account's live data is server state in the Apollo cache
 * (hooks/useAccount); this mirrors what localStorage holds.
 */
export const sessionVar = makeVar<Session | null>(getSession());

export const useSession = (): Session | null => useReactiveVar(sessionVar);

export const replaceSession = (session: Session | null) => {
  sessionVar(session);
};

/**
 * Sign-in / password-reset flow flags. Kept separate from the session so
 * the "no session" state stays an honest null instead of an empty object
 * pretending to be a Session.
 */
export interface AuthFlowState {
  isFetching: boolean;
  isRequestingPasswordReset: boolean;
  isPasswordResetRequested: boolean;
  isResettingPassword: boolean;
  isPasswordResetConfirmed: boolean;
  isAccountDeactivated: boolean;
}

const AUTH_FLOW_IDLE: AuthFlowState = {
  isFetching: false,
  isRequestingPasswordReset: false,
  isPasswordResetRequested: false,
  isResettingPassword: false,
  isPasswordResetConfirmed: false,
  isAccountDeactivated: false,
};

export const authFlowVar = makeVar<AuthFlowState>(AUTH_FLOW_IDLE);

export const useAuthFlow = (): AuthFlowState => useReactiveVar(authFlowVar);

export const patchAuthFlow = (patch: Partial<AuthFlowState>) => {
  authFlowVar({ ...authFlowVar(), ...patch });
};

export const resetAuthFlow = () => authFlowVar(AUTH_FLOW_IDLE);

/**
 * Global error list feeding the ErrorReporter toasts.
 */
export const errorsVar = makeVar<Error[]>([]);

export const useErrors = (): Error[] => useReactiveVar(errorsVar);

// Accumulate (bounded) instead of keeping only the most recent error the way
// the legacy reducer did — two failures in the same tick both surface.
const MAX_REPORTED_ERRORS = 5;
export const reportError = (error: Error) =>
  errorsVar([...errorsVar(), error].slice(-MAX_REPORTED_ERRORS));

export const discardError = (error: Error) => errorsVar(errorsVar().filter(x => x !== error));

export const discardAllErrors = () => errorsVar([]);

/**
 * Toast notification message ('' = hidden).
 */
export const toastVar = makeVar<string>('');

export const useToastNotification = (): string => useReactiveVar(toastVar);

export const notifyToast = (message: string) => toastVar(message);

export const discardToast = () => toastVar('');

/**
 * Dashboard chrome state (mobile menu, notification hub, sidebar tool).
 */
export interface DashboardConfiguration {
  isRightSidebarOpen?: boolean;
  isMenuOpen?: boolean;
  isNotifHubOpen?: boolean;
  tool?: SidebarTool;
}

export const dashboardConfigVar = makeVar<DashboardConfiguration>({ isNotifHubOpen: true });

export const useDashboardConfiguration = (): DashboardConfiguration =>
  useReactiveVar(dashboardConfigVar);

const patchDashboardConfig = (patch: Partial<DashboardConfiguration>) => {
  dashboardConfigVar({ ...dashboardConfigVar(), ...patch });
};

export const openSidebar = (tool: SidebarTool) =>
  patchDashboardConfig({ isRightSidebarOpen: true, tool });

export const closeSidebar = () => patchDashboardConfig({ isRightSidebarOpen: false });

export const openMenu = () => patchDashboardConfig({ isMenuOpen: true });

export const closeMenu = () => patchDashboardConfig({ isMenuOpen: false });

export const dismissNotifHub = () => patchDashboardConfig({ isNotifHubOpen: false });
